import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Send, Loader2, CheckCircle2, MessageCircle, Gift } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { company, serviceOptions } from "../mock";
import { useToast } from "../hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: serviceOptions[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Missing details", description: "Please fill in your name, email and message.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast({ title: "Message sent!", description: "Thank you — our team will be in touch shortly." });
    } catch (err) {
      toast({ title: "Could not send", description: "Please try again or WhatsApp us directly.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const waLink = `https://wa.me/${company.whatsapp}?text=${encodeURIComponent("Hi BEE Exsellance, I'd like to make an enquiry.")}`;

  const info = [
    { icon: MapPin, title: "Office Address", lines: [company.address] },
    { icon: Phone, title: "Phone Numbers", lines: company.phones, hrefs: company.phones.map((p) => `tel:${p.replace(/\s/g, "")}`) },
    { icon: Mail, title: "E-mail", lines: [company.email], hrefs: [`mailto:${company.email}`] },
    { icon: Clock, title: "Office Hours", lines: [company.hours] },
  ];

  return (
    <div>
      <PageHeader
        title="Contact Us"
        crumb="Contact"
        subtitle="How can we be of service? Feel free to call, send an email, or arrange for an appointment."
      />

      <section className="py-20 md:py-24">
        <div className="container-x grid lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2 space-y-5">
            <Reveal>
              <div className="eyebrow mb-3">Get In Touch</div>
              <h2 className="font-head text-2xl md:text-3xl font-bold text-[#0B1F3A]">Let's start the conversation</h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                For general enquiries please fill out the form. For quote-related questions, contact us for a free quote.
              </p>
              <div className="mt-4 flex items-center gap-3 rounded-2xl bg-[#0B1F3A] text-white p-4" data-testid="contact-free-consult">
                <div className="w-10 h-10 rounded-xl bg-[#D4AF37] grid place-items-center text-[#0B1F3A] shrink-0">
                  <Gift size={20} />
                </div>
                <div>
                  <div className="font-head font-semibold text-[15px]">Your first consultation is free of charge</div>
                  <div className="text-[13px] text-slate-300">Book a no-obligation session with our B-BBEE experts.</div>
                </div>
              </div>
            </Reveal>
            <div className="grid gap-4">
              {info.map((it, idx) => {
                const Icon = it.icon;
                return (
                  <Reveal key={it.title} delay={idx * 70}>
                    <div className="flex gap-4 bg-white rounded-2xl border border-slate-100 p-5 card-lift">
                      <div className="w-12 h-12 rounded-xl bg-[#0B1F3A] grid place-items-center text-[#D4AF37] shrink-0">
                        <Icon size={22} />
                      </div>
                      <div>
                        <h4 className="font-head font-semibold text-[#0B1F3A] mb-1">{it.title}</h4>
                        {it.lines.map((ln, i) => (
                          <div key={ln} className="text-sm text-slate-600">
                            {it.hrefs ? (
                              <a href={it.hrefs[i]} className="hover:text-[#184E9E] transition-colors">{ln}</a>
                            ) : ln}
                          </div>
                        ))}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <a href={waLink} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-semibold py-3.5 rounded-2xl transition-colors">
              <MessageCircle size={20} /> Chat with us on WhatsApp
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <Reveal>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_50px_-30px_rgba(10,26,52,.3)] p-7 md:p-9">
                <h3 className="font-head font-bold text-xl text-[#0B1F3A] mb-1">Send Us a Message</h3>
                <p className="text-sm text-slate-500 mb-6">We usually respond within one business day.</p>

                {sent ? (
                  <div className="text-center py-12">
                    <CheckCircle2 size={52} className="text-emerald-500 mx-auto mb-4" />
                    <h4 className="font-head font-bold text-xl text-[#0B1F3A]">Thank you!</h4>
                    <p className="text-slate-500 mt-2">Your message has been received. We'll be in touch shortly.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Full Name *" value={form.name} onChange={(v) => set("name", v)} placeholder="Your name" />
                      <Field label="Email *" type="email" value={form.email} onChange={(v) => set("email", v)} placeholder="you@company.com" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Phone" value={form.phone} onChange={(v) => set("phone", v)} placeholder="+27 ..." />
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Select Your Service</label>
                        <select
                          value={form.service}
                          onChange={(e) => set("service", e.target.value)}
                          className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-[15px] bg-white outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition"
                        >
                          {serviceOptions.map((o) => <option key={o}>{o}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Message *</label>
                      <textarea
                        rows={5}
                        value={form.message}
                        onChange={(e) => set("message", e.target.value)}
                        placeholder="How can we help?"
                        className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition resize-none"
                      />
                    </div>
                    <button type="submit" disabled={loading}
                      className="btn-gold w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg disabled:opacity-70">
                      {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                      {loading ? "Sending…" : "Send Message"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-20">
        <div className="container-x">
          <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm h-[360px]">
            <iframe
              title="BEE Exsellance location"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              src="https://www.google.com/maps?q=533%20Long%20Avenue%2C%20Ferndale%2C%20Randburg%2C%202194&output=embed"
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-200 px-3.5 py-2.5 text-[15px] outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition"
      />
    </div>
  );
}
